import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal  } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { saveAs } from 'Core/utils';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { selectStatusLoading } from '../../../core/redux/status/selectors';
import { CustomerService } from '../../../service/api/customer-service';
const { CUSTOMERS_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'customers';
export const INITIAL_STATE = {
  data: [],  
  loading: false,
  error: null,
  product: null,
  printersdata: [],
  filters: {
    status: {
      value: 'In Customer', 
      label: 'In Customer' 
    },
    searchText: ''
  }, 
  labelsList: [],
  filterVisible: false,
  tileView: true,
  selectedCustomer: null,
  editModalVisible: false,  
  
};

export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await CustomerService.getAllCustomersList(filters);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Customers');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Customers');
    }
  },
);

export const fetchLabels = createAsyncThunk(
  'customers/fetchLabels',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await CustomerService.getLabels();
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Labels');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Labels');
    }
  },
);

export const addCustomer = createAsyncThunk(
  'customers/addCustomer',
  async (customerPayload, { rejectWithValue }) => {
    try {
      const response = await CustomerService.createCustomer(customerPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add Customers');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add Customers');
    }
  },
);

export const editCustomer = createAsyncThunk(
  'customers/editCustomer',
  async (customerPayload, { rejectWithValue }) => {
    try {
      const response = await CustomerService.createCustomer(customerPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update customer');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update customer');
    }
  },
);

export const downloadCustomers = createAsyncThunk(
  'customers/downloadCustomers',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await CustomerService.downloadCustomer(filters);
      const blob = response;
      saveAs(blob, `Customers.xlsx`);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to download Customers');
    }
  },
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await CustomerService.deleteCustomer(customerId);
      if (response.status === 200) {
        return customerId;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to delete customer');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete customer');
    }
  },
);

export const fetchCustomersWithFilters = () => (dispatch, getState) => {
  const filters = getState()[STORE_KEY].filters;
  dispatch(
    fetchCustomers({
      searchText: filters.searchText,
      status: filters.status.value
    }),
  );
};

export const downloadCustomersWithFilters = () => (dispatch, getState) => {
  const filters = getState()[STORE_KEY].filters;
  dispatch(
    downloadCustomers({
      searchText: filters.searchText,
      customer: filters?.customer?.value,
      status: filters?.status?.value,
      customerDate: filters?.shipDate,
    }),
  );
};

export const { reducer, actions } = createSlice({
  name: 'customers',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setProduct:(state, action) => {
      state.product = action.payload;
    },
    resetFilters: (state) => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setCustomer: (state, action) => {
      state.filters.customer = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setStartDate: (state, action) => {
      state.filters.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.filters.endDate = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload; // Add reducer to update tileView
    },     
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCustomers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addCustomer.fulfilled, (state, action) => {
        const newCustomer = {
          ...action.payload,
          isNewlyCreated: true,
        };
        state.data = [newCustomer, ...state.data];
      })
      .addCase(fetchLabels.fulfilled, (state, action) => {
        state.labelsList = action.payload;
      })
      .addCase(addCustomer.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editCustomer.fulfilled, (state, action) => {
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editCustomer.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        const newData = state.data.filter(s => s.id !== action.payload);
        state.data = newData;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addCustomer.rejected, editCustomer.rejected
  ),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addCustomer.fulfilled, editCustomer.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(CUSTOMERS_ADD_EDIT));
    listenerApi.dispatch(fetchCustomersWithFilters());
    listenerApi.dispatch(
      showToast({
        message: 'Customer saved successfully',
        type: 'success',
      }),
    );
  },
});


redux.listener.startListening({
  matcher: isAnyOf(editCustomer.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(CUSTOMERS_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Customer updated successfully',
        type: 'success',
      }),
    );
  },
});
redux.listener.startListening({
  matcher: isAnyOf(addCustomer.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(CUSTOMERS_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Customer saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(deleteCustomer.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Customer deleted successfully',
        type: 'success',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);
